// ============================================================================
// Re-export backend types (single source of truth)
// ============================================================================

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();export {
	AutomationStatus,
	AutomationTriggerType,
	ExecutionStatus,
} from "@/lib/services/automation/types";
export { ProviderType } from "@/types/dataProvider";

import type {
	AutomationStatus,
	AutomationTriggerType,
	ExecutionStatus,
	StepExecutionResult,
} from "@/lib/services/automation/types";
import type { ProviderType } from "@/types/dataProvider";

export enum EtlStepKind {
	PULL = "PULL",
	TRANSFORM = "TRANSFORM",
	PUSH = "PUSH",
}

// ============================================================================
// Operation Types (Predefined Transformations)
// ============================================================================

export enum EtlOperationType {
	// Invoice operations
	APPFOLIO_TO_QB_INVOICE_PDF = "APPFOLIO_TO_QB_INVOICE_PDF",
	QB_TO_APPFOLIO_UPLOAD_PDF = "QB_TO_APPFOLIO_UPLOAD_PDF",
	// Add more as needed
}

export interface EtlOperation {
	operationType: EtlOperationType;
	displayName: string;
	description: string;
	isActive: boolean;
	// Which source providers this operation supports
	supportedSourceProviders: ProviderType[];
	// Which destination providers this operation supports
	supportedDestinationProviders: ProviderType[];
	// Whether this operation supports multiple sources
	supportsMultipleSources: boolean;
}

// ============================================================================
// Step Configuration Types
// ============================================================================

export interface EtlPullStepConfig {
	// One or more source providers
	sourceProviders: ProviderType[];
	// Provider-specific dataset keys (e.g., "INVOICES", "PROPERTIES")
	datasetKeys: string[];
	// Optional filters/constraints
	filters?: Record<string, unknown>;
}

export interface EtlTransformStepConfig {
	// The selected operation types (array to support multi-select)
	operationTypes: EtlOperationType[];
	// Operation-specific configuration
	operationConfig?: Record<string, unknown>;
}

export interface EtlPushStepConfig {
	// Destination providers (array to support multi-select)
	destinationProviders: ProviderType[];
	// Destination-specific configuration
	destinationConfig?: Record<string, unknown>;
}

// ============================================================================
// Automation Step Types
// ============================================================================

export interface EtlAutomationStep {
	id: string;
	automationId: string;
	stepOrder: number; // 1, 2, 3
	stepKind: EtlStepKind;
	stepName: string;
	// Step-specific identifier (e.g., "APPFOLIO_PULL_INVOICES", "CANONICALIZE_INVOICES")
	stepKey: string;
	// Provider keys if applicable (empty array for TRANSFORM)
	provider: ProviderType[];
	// Step-specific configuration
	config: EtlPullStepConfig | EtlTransformStepConfig | EtlPushStepConfig;
	status: "ENABLED" | "DISABLED";
	createdAt: string;
	updatedAt: string;
}

// ============================================================================
// Automation Types
// ============================================================================

export interface EtlAutomationWebhookConfig {
	enabled: boolean;
	events?: string[];
	provider?: string;
}

export interface EtlAutomationMetadata {
	sourceProviders: ProviderType[];
	destinationProviders: ProviderType[];
	orchestratorId?: string;
	schedulerJobCreated?: boolean;
	schedulerJobCreatedAt?: Date;
	schedulerJobError?: string;
	skillDefinitionId?: string;
	tierSyncPausedAt?: string;
	webhook?: EtlAutomationWebhookConfig;
}

export interface EtlAutomation {
	id: string;
	organizationId?: string;
	workspaceId?: string;
	operationId?: string;
	name: string;
	description: string;
	status: AutomationStatus;
	triggerType: AutomationTriggerType;
	// Cron expression if triggerType is SCHEDULED
	scheduleCron: string | null;
	// Timezone for scheduled automations
	timezone: string;
	// User-configurable overrides (safe knobs only)
	config: Record<string, unknown>;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
	// Steps are stored separately but referenced here
	steps: EtlAutomationStep[];
	metadata: EtlAutomationMetadata;
}

// ============================================================================
// Create/Update Input Types
// ============================================================================

export interface CreateEtlAutomationInput {
	name: string;
	description?: string;
	operationId: string;
	workspaceId: string;
	triggerType: AutomationTriggerType;
	scheduleCron?: string | null;
	timezone?: string;
	config?: Record<string, unknown>;
	status?: AutomationStatus;
	// Step configurations (UI only, not sent to API)
	pullStep?: EtlPullStepConfig;
	transformStep?: EtlTransformStepConfig;
	pushStep?: EtlPushStepConfig;
}

export interface UpdateEtlAutomationInput {
	id: string;
	workspaceId: string;
	name?: string;
	status?: AutomationStatus;
	triggerType?: AutomationTriggerType;
	scheduleCron?: string | null;
	timezone?: string;
	config?: Record<string, unknown>;
	operationId?: string;
}

// ============================================================================
// Execution Types (uses backend ExecutionStatus)
// ============================================================================

export interface EtlAutomationExecution {
	id: string;
	organizationId: string;
	automationId: string;
	status: ExecutionStatus;
	triggeredBy: "MANUAL" | "SCHEDULE" | "SYSTEM";
	triggeredByUser: string | null;
	startedAt: string | null;
	finishedAt: string | null;
	recordsConsidered: number;
	recordsSkipped: number;
	recordsSucceeded: number;
	recordsFailed: number;
	errorSummary: string | null;
	createdAt: string;
	stepExecutions?: StepExecutionResult[];
}
